// Markdown-like → HTML converter for article content. Handles tables, headings
// (with anchor IDs for TOC), images, links, bold/italic, blockquotes, lists,
// paragraphs, and `data-speakable` annotations for SEO.

export const renderContent = (content) => {
  if (!content) return '<p>Content coming soon...</p>';

  let html = content;

  // Markdown tables → <table>
  html = html.replace(/((?:^\|.+\|[ \t]*\n)+)/gm, (tableBlock) => {
    const rows = tableBlock.trim().split('\n').filter((r) => r.trim());
    if (rows.length < 2) return tableBlock;
    const parseRow = (row) => row.split('|').slice(1, -1).map((c) => c.trim());
    const headerCells = parseRow(rows[0]);
    const isSep = /^[\s|:-]+$/.test(rows[1]);
    const dataStart = isSep ? 2 : 1;
    const thead = '<thead><tr>' + headerCells.map((c) => `<th>${c}</th>`).join('') + '</tr></thead>';
    const tbody = '<tbody>' + rows.slice(dataStart).map((r) => {
      const cells = parseRow(r);
      return '<tr>' + cells.map((c) => `<td>${c}</td>`).join('') + '</tr>';
    }).join('') + '</tbody>';
    return `<div class="article-table-wrap"><table class="article-table">${thead}${tbody}</table></div>`;
  });

  // Headings with anchor IDs (TOC support)
  html = html.replace(/^### (.+)$/gm, (_, text) => {
    const id = text.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    return `<h3 class="article-h3" id="${id}">${text}</h3>`;
  });
  html = html.replace(/^## (.+)$/gm, (_, text) => {
    const id = text.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    return `<h2 class="article-h2" id="${id}">${text}</h2>`;
  });

  // Images
  html = html.replace(/!\[([^\]]*)\]\(([^)]+)\)/g,
    '<img src="$2" alt="$1" class="article-image w-full rounded-xl my-8 shadow-lg" loading="lazy" />');

  // Links (external + internal)
  html = html.replace(/\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g,
    '<a href="$2" class="text-[#E63946] hover:text-[#c62d39] underline underline-offset-2 transition-colors font-medium" target="_blank" rel="noopener noreferrer">$1</a>');
  html = html.replace(/\[([^\]]+)\]\((\/[^)]+)\)/g,
    '<a href="$2" class="text-[#E63946] hover:text-[#c62d39] underline underline-offset-2 transition-colors font-medium">$1</a>');

  // Emphasis
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');

  // Blockquotes
  html = html.replace(/^> (.+)$/gm, '<blockquote class="article-quote">$1</blockquote>');

  // Bullet lists
  html = html.replace(/^- (.+)$/gm, '<li class="article-li">$1</li>');
  html = html.replace(/(<li class="article-li">.*<\/li>\n?)+/g, '<ul class="article-ul">$&</ul>');

  // Numbered lists
  html = html.replace(/^\d+\. (.+)$/gm, '<li class="article-li">$1</li>');

  // Paragraphs (skip blocks that already start with HTML)
  html = html.split('\n\n').map((para) => {
    if (para.trim().startsWith('<')) return para;
    if (para.trim() === '') return '';
    return `<p class="article-p">${para}</p>`;
  }).join('\n');

  // Clean up empty paragraphs
  html = html.replace(/<p class="article-p">[\s\n]*<\/p>/g, '');

  // SEO: speakable annotations
  html = html.replace(/class="direct-answer"/g, 'class="direct-answer" data-speakable="true"');
  let firstPTagged = false;
  html = html.replace(/<p class="article-p">/g, (match) => {
    if (!firstPTagged) {
      firstPTagged = true;
      return '<p class="article-p" data-speakable="true">';
    }
    return match;
  });

  return html;
};
